import React, { useCallback, useMemo, useState } from "react";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { useMutation } from "@apollo/client";
import {
  REGISTER_AD_FROM_REGA,
  ISSUE_BROKER_AD_LICENSE,
} from "@/graphql/mutations/listing";
import { ErrorModal } from "@/components/error/ssr";
import css from "./.module.scss";
import { Button, Radio, Select, Spinner } from "@aqar-app/react-ui";
import { Checkbox, Switch } from "@aqar-app/aqar-design-system";
import Head from "next/head";
import styles from "@/src/pages/add-rega-listing/addRegaListings.module.scss";
import { TestLicenses } from "@/components/add-v2/form/regaMapping";
import { useRouter } from "next/router";
import { InitialResolver } from "@/lib/use.middlewares";
import withAuthentication from "@/hoc/withAuthentication";
import { handleOnPasteCaptureNumbers } from "@/lib/numbersOnPase";
import FieldError from "@/components/field-error/FieldError";
import {
  trackAdLicenseCheck,
  trackAdLicenseContinue,
  trackAdLicenseSuccess,
  trackIssueAdLicense,
} from "@/lib/tracking/trackAddServiceOptions";
const dev = process.env.NODE_ENV === "development";

const YES_LABEL = {
  label: "نعم",
  value: true,
};
const NO_LABEL = {
  label: "لا",
  value: false,
};

const FreeLicenseForm = ({ loading, register, router }) => {
  const [formData, setFormData] = useState({
    brokerage_contract_number: "",
    deed_number: "",
    attorney_number: "",
    price: "",
    type: "",
    acceptTerms: false,
    isAttorney: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const { token } = useAuthState();
  const [issueBrokerAdLicense] = useMutation(ISSUE_BROKER_AD_LICENSE, {
    context: { token },
  });

  const validateBrokerageContractNumber = (value) => {
    const errorMessage = "يرجى إدخال رقم عقد الوساطة بشكل صحيح ";
    if (!value || !/^\d+$/.test(value) || value.length !== 10) {
      return errorMessage;
    }

    const firstTwoDigits = value.substring(0, 2);
    if (firstTwoDigits !== "61" && firstTwoDigits !== "62") {
      return errorMessage;
    }
    return null;
  };

  const validateDeedNumber = (value) => {
    if (!value) return "رقم الصك مطلوب";
    if (!/^\d+$/.test(value)) return "يجب أن يكون الرقم أرقام فقط";
    if (value.length !== 12 && value.length !== 16) {
      return "يجب أن يكون رقم الصك 12 أو 16 رقم";
    }
    return null;
  };

  const validateAttorneyNumber = (value, isAttorney) => {
    if (isAttorney && !value) return " يرجى إدخال رقم الوكالة";
    return null;
  };

  const validatePrice = (value) => {
    if (!value) return "يرجى إدخال سعر العقار";
    if (!/^\d+$/.test(value)) return "يجب أن يكون الرقم أرقام فقط";
    if (Number(value) <= 0) return "السعر يجب أن يكون أكبر من صفر";
    return null;
  };

  const validateType = (value) => {
    if (!value) return "يرجى اختيار نوع الإعلان";
    return null;
  };

  const validateTerms = (value) => {
    if (!value) return "يجب قبول شروط والأحكام";
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    const brokerageError = validateBrokerageContractNumber(
      formData.brokerage_contract_number,
    );
    if (brokerageError) newErrors.brokerage_contract_number = brokerageError;

    const deedError = validateDeedNumber(formData.deed_number);
    if (deedError) newErrors.deed_number = deedError;

    const attorneyError = validateAttorneyNumber(
      formData.attorney_number,
      formData.isAttorney,
    );
    if (attorneyError) newErrors.attorney_number = attorneyError;

    const priceError = validatePrice(formData.price);
    if (priceError) newErrors.price = priceError;

    // const typeError = validateType(formData.type);
    // if (typeError) newErrors.type = typeError;

    const termsError = validateTerms(formData.acceptTerms);
    if (termsError) newErrors.acceptTerms = termsError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFieldBlur = (fieldName) => {
    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    // Validate the field based on its type
    let error = null;
    if (fieldName === "brokerage_contract_number") {
      error = validateBrokerageContractNumber(
        formData.brokerage_contract_number,
      );
    } else if (fieldName === "deed_number") {
      error = validateDeedNumber(formData.deed_number);
    } else if (fieldName === "attorney_number") {
      error = validateAttorneyNumber(
        formData.attorney_number,
        formData.isAttorney,
      );
    } else if (fieldName === "price") {
      error = validatePrice(formData.price);
    } else if (fieldName === "type") {
      error = validateType(formData.type);
    } else if (fieldName === "acceptTerms") {
      error = validateTerms(formData.acceptTerms);
    }

    // Update errors
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleCheckboxChange = (val) => {
    handleFieldChange("acceptTerms", val);
  };

  const handleSubmit = () => {
    if (validateForm()) {
      trackIssueAdLicense({});
      issueBrokerAdLicense({
        variables: {
          data: {
            brokerage_contract_number: formData.brokerage_contract_number,
            deed_number: formData.deed_number,
            attorney_number: formData.attorney_number,
            property_price: parseFloat(formData.price),
            advertisement_type: formData.type,
          },
        },
      })
        .then(({ data }) => {
          // On success, call register function similar to renderHasLicenseForm
          register(
            {
              ad_license_number:
                data?.RegaListing?.issueBrokerAdLicense?.ad_license_number,
              advertiser_id:
                data?.RegaListing?.issueBrokerAdLicense?.advertiser_id,
            },
            (licenseData) => {
              localStorage.setItem(
                "newRegaListingDraft",
                JSON.stringify({
                  ctx: {
                    category: 0,
                  },
                  regaResponse: licenseData,
                }),
              );

              trackAdLicenseSuccess({});
              return router.replace(`/add-listing/rega/summery?`);
            },
          );
        })
        .catch((e) => {
          // On error, display error modal
          ErrorModal.handle(e, null);
        });
    }
  };

  const isFormValid =
    formData.brokerage_contract_number &&
    validateBrokerageContractNumber(formData.brokerage_contract_number) ===
      null &&
    formData.deed_number &&
    validateDeedNumber(formData.deed_number) === null &&
    (!formData.isAttorney ||
      (formData.attorney_number &&
        validateAttorneyNumber(
          formData.attorney_number,
          formData.isAttorney,
        ) === null)) &&
    formData.price &&
    validatePrice(formData.price) === null &&
    formData.type &&
    validateType(formData.type) === null &&
    formData.acceptTerms === true;

  return (
    <div className={css.root}>
      <h1>إضافة ترخيص إعلان</h1>
      <div className={`${css.form} ${css.freeLicenseForm}`}>
        <div className={css.group}>
          <label htmlFor="brokerage_contract_number">رقم عقد الوساطة *</label>
          <input
            id="brokerage_contract_number"
            inputMode="numeric"
            placeholder="أدخل هنا..."
            onPasteCapture={handleOnPasteCaptureNumbers}
            onKeyDown={(e) => {
              if (
                e.ctrlKey ||
                e.metaKey ||
                e.key === "Tab" ||
                e.key === "Backspace"
              )
                return;
              const nextValue = e.target.value + e.key;
              if (Number.isNaN(+nextValue)) return e.preventDefault();
            }}
            onChange={(e) => {
              if (e.target.value.length > 10) return;
              handleFieldChange("brokerage_contract_number", e.target.value);
            }}
            onBlur={() => handleFieldBlur("brokerage_contract_number")}
            value={formData.brokerage_contract_number}
            className={
              errors.brokerage_contract_number &&
              touched.brokerage_contract_number
                ? css.error
                : ""
            }
          />
          {errors.brokerage_contract_number &&
            touched.brokerage_contract_number && (
              <FieldError text={errors.brokerage_contract_number} />
            )}
        </div>

        <div className={css.group}>
          <label htmlFor="deed_number">رقم الصك *</label>
          <input
            id="deed_number"
            inputMode="numeric"
            placeholder="أدخل هنا..."
            onPasteCapture={handleOnPasteCaptureNumbers}
            onKeyDown={(e) => {
              if (
                e.ctrlKey ||
                e.metaKey ||
                e.key === "Tab" ||
                e.key === "Backspace"
              )
                return;
              const nextValue = e.target.value + e.key;
              if (Number.isNaN(+nextValue)) return e.preventDefault();
            }}
            onChange={(e) => {
              if (e.target.value.length > 16) return;
              handleFieldChange("deed_number", e.target.value);
            }}
            onBlur={() => handleFieldBlur("deed_number")}
            value={formData.deed_number}
            className={
              errors.deed_number && touched.deed_number ? css.error : ""
            }
          />
          {errors.deed_number && touched.deed_number && (
            <FieldError text={errors.deed_number} />
          )}
        </div>

        <div className={css.group}>
          <label htmlFor="price">سعر العقار *</label>
          <div className={css.currencyInput}>
            <input
              id="price"
              inputMode="numeric"
              placeholder="أدخل هنا..."
              onPasteCapture={handleOnPasteCaptureNumbers}
              onKeyDown={(e) => {
                if (
                  e.ctrlKey ||
                  e.metaKey ||
                  e.key === "Tab" ||
                  e.key === "Backspace"
                )
                  return;
                const nextValue = e.target.value + e.key;
                if (Number.isNaN(+nextValue)) return e.preventDefault();
              }}
              onChange={(e) => {
                handleFieldChange("price", e.target.value);
              }}
              onBlur={() => handleFieldBlur("price")}
              value={formData.price}
              className={errors.price && touched.price ? css.error : ""}
            />
            <span className={css.currencyLabel}>ريال</span>
          </div>
          {errors.price && touched.price && <FieldError text={errors.price} />}
        </div>

        <div className={`${css.group} ${css.selectGroup}`}>
          <label htmlFor="type">نوع الإعلان *</label>
          <Select
            id="type"
            value={formData.type}
            searchable={false}
            onSelect={(val) => {
              handleFieldChange("type", val);
            }}
            onBlur={() => handleFieldBlur("type")}
            className={errors.type && touched.type ? css.error : ""}
          >
            <Select.Option value="">
              <p>الرجاء الاختيار</p>
            </Select.Option>
            <Select.Option value="Sell">
              <p>بيع</p>
            </Select.Option>
            <Select.Option value="Rent">
              <p>إيجار</p>
            </Select.Option>
          </Select>
          {errors.type && touched.type && <FieldError text={errors.type} />}
        </div>

        <div className={css.group}>
          <label className={css.switchLabel}>
            <span>هل أنت وكيل؟</span>
            <Switch
              checked={formData.isAttorney}
              onChange={(value) => {
                handleFieldChange("isAttorney", value);
              }}
            />
          </label>
        </div>

        {formData.isAttorney && (
          <div className={css.group}>
            <label htmlFor="attorney_number">رقم الوكالة *</label>
            <input
              id="attorney_number"
              placeholder="أدخل هنا..."
              onChange={(e) => {
                handleFieldChange("attorney_number", e.target.value);
              }}
              onBlur={() => handleFieldBlur("attorney_number")}
              value={formData.attorney_number}
              className={
                errors.attorney_number && touched.attorney_number
                  ? css.error
                  : ""
              }
            />
            {errors.attorney_number && touched.attorney_number && (
              <FieldError text={errors.attorney_number} />
            )}
          </div>
        )}

        <div className={css.group}>
          <label>
            <Checkbox
              className={css.checkboxLabel}
              checkboxClassName={`${css.checkbox} ${formData.acceptTerms ? css.checked : ""}`}
              label={
                <span style={{ textWrap: "auto" }}>
                  اتعهد باستخدام هذا الترخيص في منصة عقار فقط، وإذا تبين
                  استخدامه في منصة أخرى فإنه يحق لمنصة عقار حذف الإعلان.
                </span>
              }
              onChange={handleCheckboxChange}
              onBlur={() => handleFieldBlur("acceptTerms")}
            />
          </label>
          {errors.acceptTerms && touched.acceptTerms && (
            <FieldError text={errors.acceptTerms} />
          )}
        </div>

        <div className={css.group}>
          <Button
            className={css.next}
            disabled={!isFormValid || loading}
            onClick={handleSubmit}
            theme="primary"
          >
            {loading ? <Spinner size="small" /> : null}
            التالي
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddRegaLicensePage = withAuthentication(function AddRegaLicensePage() {
  const [loading, setLoading] = useState(false);
  const [hasLicense, setHasLicense] = useState(undefined);
  const [showLicenseForm, setShowLicenseForm] = useState(false);

  const router = useRouter();
  const { token } = useAuthState();
  const [licenseData, setLicenseData] = useState({
    ad_license_number: null,
    advertiser_id: null,
    id_type: null,
  });
  const [registerRegaListing] = useMutation(REGISTER_AD_FROM_REGA, {
    context: { token },
  });
  const isDevelopment = useMemo(() => {
    if (typeof window === "undefined") return dev;
    return location.href.startsWith("https://next.aqar.fm") || dev;
  }, []);

  // const { free_licensing_eligible: isFreeLicenseEligible } = user || {};
  const isFreeLicenseEligible = true;

  const register = useCallback(function (data, onData) {
    const { ad_license_number, advertiser_id } = data;
    setLoading(true);
    setLicenseData({
      ad_license_number,
      advertiser_id,
      id_type: ad_license_number?.[1] || 1,
    });
    registerRegaListing({
      variables: {
        data: {
          ad_license_number,
          advertiser_id,
          id_type: ad_license_number?.[1] || 1,
        },
      },
    })
      .then(({ data }) => {
        onData(data?.RegaListing?.registerAdFromRega || {});
      })
      .catch((e) => {
        ErrorModal.handle(e, null);
      })
      .finally(() => setLoading(false));
  }, []);

  const renderHasLicenseForm = () => {
    return (
      <>
        <div className={css.group}>
          <p>رقم ترخيص الإعلان *</p>
          <input
            inputMode="numeric"
            onPasteCapture={handleOnPasteCaptureNumbers}
            onKeyDown={(e) => {
              if (
                e.ctrlKey ||
                e.metaKey ||
                e.key === "Tab" ||
                e.key === "Backspace"
              )
                return;

              const nextValue = e.target.value + e.key;

              if (Number.isNaN(+nextValue)) return e.preventDefault();
            }}
            onChange={(e) => {
              if (e.target.value.length > 10) return;
              setLicenseData({
                ...licenseData,
                ad_license_number: e.target.value,
                id_type: licenseData.ad_license_number?.[1] || 1,
              });
            }}
            value={licenseData.ad_license_number}
          />
        </div>
        {licenseData.ad_license_number?.length > 9 && (
          <div className={css.group}>
            <p>
              {licenseData?.id_type + "" === "1"
                ? "رقم هوية المعلن"
                : "رقم سجل المنشأة"}
            </p>
            <input
              inputMode="numeric"
              onPasteCapture={handleOnPasteCaptureNumbers}
              onKeyDown={(e) => {
                if (
                  e.ctrlKey ||
                  e.metaKey ||
                  e.key === "Tab" ||
                  e.key === "Backspace"
                )
                  return;
                const nextValue = e.target.value + e.key;
                if (Number.isNaN(+nextValue)) return e.preventDefault();
              }}
              onChange={(e) => {
                if (e.target.value.length > 10) return;
                setLicenseData({
                  ...licenseData,
                  advertiser_id: e.target.value,
                });
              }}
              value={licenseData.advertiser_id}
            />
          </div>
        )}
        <div className={css.group}>
          <Button
            className={css.next}
            disabled={
              loading ||
              !licenseData.ad_license_number ||
              licenseData.ad_license_number.length < 10 ||
              !licenseData.advertiser_id ||
              licenseData.advertiser_id.length < 10
            }
            onClick={() => {
              trackAdLicenseContinue({ inputData: "using_license" });
              register(licenseData, (data) => {
                localStorage.setItem(
                  "newRegaListingDraft",
                  JSON.stringify({
                    ctx: {
                      category: 0,
                    },
                    regaResponse: data,
                  }),
                );

                return router.replace(`/add-listing/rega/summery`);
              });
            }}
            theme="primary"
          >
            {loading ? <Spinner size="small" /> : null}
            التالي
          </Button>
        </div>
      </>
    );
  };

  const renderNoLicenseForm = () => {
    return (
      <div className={css.group}>
        <div className={css.serviceDescription}>
          <p>
            تتيح منصة عقار للوسطاء المرخصين إصدار ترخيص إعلان مجاني ونشره داخل
            منصة عقار، تتطلب الخدمة:
          </p>
          <ul>
            <li>وجود عقد وساطة.</li>
            <li>التعهد باستخدام الترخيص داخل منصة عقار فقط.</li>
          </ul>
        </div>
        <Button
          className={css.next}
          onClick={() => {
            trackAdLicenseContinue({ inputData: "using_deed" });
            setShowLicenseForm(true);
          }}
          theme="primary"
        >
          التالي
        </Button>
      </div>
    );
  };

  if (showLicenseForm) {
    return (
      <FreeLicenseForm loading={loading} register={register} router={router} />
    );
  }

  return (
    <div className={css.root}>
      <Head>
        <title>إضافة إعلان مرخص | عقار</title>
      </Head>
      <h1>رخصة إعلان</h1>
      <div className={css.form}>
        {isFreeLicenseEligible ? (
          <p
            className={css.info}
            style={{
              maxWidth: "376px",
              justifySelf: "center",
              fontSize: "14px",
              color: "#4D4D4D",
            }}
          >
            لإضافة إعلان في منصة عقار يجب أن يكون لديك ترخيص إعلان صادر من
            الهيئة العامة للعقار
          </p>
        ) : (
          <p className={css.info}>
            بناء على نظام الوساطة العقارية لايمكن الاعلان في المنصات العقارية
            الا بوجود رخصة اعلان.
            <a
              href="https://aqr.to/ad-how-to"
              target="_blank"
              rel="noreferrer"
              className={styles.what}
            >
              ماهي رخصة الاعلان وكيفية الحصول عليها؟
            </a>
          </p>
        )}
        {isDevelopment && !isFreeLicenseEligible && (
          <div className={css.group}>
            <p>للتطوير فقط</p>
            <Radio
              button
              onChange={(e, raw) => {
                register(raw, (data) => {
                  localStorage.setItem(
                    "newRegaListingDraft",
                    JSON.stringify({
                      ctx: { category: 0 },
                      regaResponse: data,
                    }),
                  );

                  return router.replace(`/add-listing/rega/summery`);
                });
              }}
              visibleOptions={3}
              name="test"
            >
              {TestLicenses.map(([ad_license_number, advertiser_id, label]) => (
                <Radio.Option
                  label={label}
                  value={ad_license_number}
                  raw={{ advertiser_id, ad_license_number }}
                />
              ))}
            </Radio>
          </div>
        )}
        {isFreeLicenseEligible && (
          <div className={css.group} style={{ paddingBottom: "8px" }}>
            <div className={css.infoRow}>
              <p>لديك ترخيص إعلان؟</p>
              <a
                href="https://aqr.to/ad-how-to"
                target="_blank"
                rel="noreferrer"
                className={styles.what}
              >
                ما هو ترخبص الإعلان؟
              </a>
            </div>
            <Radio
              button
              onChange={(e, raw) => {
                trackAdLicenseCheck({
                  target: raw ? "using_license" : "using_deed",
                });
                setHasLicense(raw);
              }}
              visibleOptions={2}
              name="haveLicense"
            >
              <Radio.Option
                label={YES_LABEL.label}
                value={YES_LABEL.value}
                raw={YES_LABEL.value}
              />
              <Radio.Option
                label={NO_LABEL.label}
                value={NO_LABEL.value}
                raw={NO_LABEL.value}
              />
            </Radio>
          </div>
        )}
        {(!isFreeLicenseEligible || (isFreeLicenseEligible && hasLicense)) &&
          renderHasLicenseForm()}
        {isFreeLicenseEligible && hasLicense === false && renderNoLicenseForm()}
      </div>
    </div>
  );
});
AddRegaLicensePage.getInitialProps = InitialResolver();
export default AddRegaLicensePage;
